import { useRef, useEffect } from 'react';

const base = import.meta.env.BASE_URL;

/** Partner logos (local assets under /public/providers). Order matches PNGGO provider lineup. */
const providers = [
  { src: `${base}providers/bng.png`, alt: 'BNG' },
  { src: `${base}providers/wf-gaming.png`, alt: 'WF Gaming' },
  { src: `${base}providers/vpower.png`, alt: 'V Power' },
  { src: `${base}providers/jili.png`, alt: 'JILI' },
  { src: `${base}providers/scr888.png`, alt: 'SCR888' },
  { src: `${base}providers/megah5.png`, alt: 'MEGA H5' },
  { src: `${base}providers/918kaya.png`, alt: '918Kaya' },
  { src: `${base}providers/advantplay.png`, alt: 'ADVANTPLAY' },
  { src: `${base}providers/cq9-gaming.png`, alt: 'CQ9 Gaming' },
  { src: `${base}providers/jdb.png`, alt: 'JDB' },
  { src: `${base}providers/live22.png`, alt: 'Live22' },
  { src: `${base}providers/lucky365.png`, alt: 'Lucky365' },
  { src: `${base}providers/joker.png`, alt: 'JOKER' },
  { src: `${base}providers/acewin.png`, alt: 'Acewin' },
  { src: `${base}providers/mario-club.png`, alt: 'MARIO CLUB' },
  { src: `${base}providers/crowd-play.png`, alt: 'Crowd Play' },
  { src: `${base}providers/uu-slots.png`, alt: 'UU SLOTS' },
  { src: `${base}providers/xe88.png`, alt: 'XE88' },
  { src: `${base}providers/slotomania.png`, alt: 'Slotomania' },
];

export default function ProvidersCarousel() {
  const trackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reduceMotion.matches) {
      track.style.transform = 'none';
      return () => {};
    }

    let pos = 0;
    let raf = 0;

    const halfWidth = () => {
      const w = track.scrollWidth;
      return w > 0 ? w / 2 : 0;
    };

    const scrollSpeed = () =>
      window.matchMedia('(max-width: 639px)').matches ? 0.38 : 0.52;

    const tick = () => {
      const half = halfWidth();
      if (half <= 0) {
        raf = requestAnimationFrame(tick);
        return;
      }
      pos += scrollSpeed();
      if (pos >= half) pos -= half;
      track.style.transform = `translate3d(-${pos}px,0,0)`;
      raf = requestAnimationFrame(tick);
    };

    const ro = new ResizeObserver(() => {
      const half = halfWidth();
      if (half > 0 && pos >= half) pos = pos % half;
    });
    ro.observe(track);

    raf = requestAnimationFrame(tick);

    const onReduceChange = () => {
      if (reduceMotion.matches) cancelAnimationFrame(raf);
    };
    reduceMotion.addEventListener('change', onReduceChange);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      reduceMotion.removeEventListener('change', onReduceChange);
    };
  }, []);

  const doubled = [...providers, ...providers];

  return (
    <section
      className="bg-[#0d0d0d] py-6 sm:py-8 overflow-hidden border-y border-white/5"
      aria-label="Game provider partners"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 mb-4 sm:mb-5">
        <p className="text-center text-[11px] sm:text-xs font-semibold tracking-[0.2em] uppercase text-white/40">
          World-class providers
        </p>
      </div>

      <div className="relative overflow-hidden">
        {/* Edge fades — keeps logos from feeling “cut off” on narrow screens */}
        <div
          className="pointer-events-none absolute inset-y-0 left-0 z-10 w-10 sm:w-14 bg-gradient-to-r from-[#0d0d0d] to-transparent"
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute inset-y-0 right-0 z-10 w-10 sm:w-14 bg-gradient-to-l from-[#0d0d0d] to-transparent"
          aria-hidden="true"
        />

        <div
          ref={trackRef}
          className="flex gap-6 sm:gap-8 md:gap-10 items-center will-change-transform"
          style={{ width: 'max-content' }}
        >
          {doubled.map((p, i) => (
            <div
              key={`${p.alt}-${i}`}
              className="flex-shrink-0 min-w-[6.75rem] sm:min-w-[7.25rem] md:min-w-[8rem] min-h-[3.25rem] sm:min-h-[3.5rem] md:min-h-[4rem] flex items-center justify-center px-1 opacity-90 sm:opacity-85 sm:hover:opacity-100 transition-opacity"
            >
              <img
                src={p.src}
                alt=""
                role="presentation"
                loading="lazy"
                decoding="async"
                className="max-h-10 sm:max-h-12 md:max-h-14 w-full max-w-[6.5rem] sm:max-w-[7rem] md:max-w-[7.5rem] object-contain select-none"
              />
            </div>
          ))}
        </div>
      </div>

      <p className="sr-only">
        Partner logos include BNG, WF Gaming, V Power, JILI, SCR888, and other certified studios available at PNGGO.
      </p>
    </section>
  );
}
